import React, { useEffect, useCallback, useState, useRef } from "react";
import { useSelector, useDispatch, useStore } from "react-redux";
import { Form, notification, Modal, Button } from "antd";
import "../../styles/Reporting.css";
import FooterButtonGroup from "../../components/common/Button/FooterButtonGroup";
import PreSurvey from "../../components/reporting/PreSurvey";
import ConfirmationModal from "../../components/common/ConfirmationModelBox/ConfirmationModelBox";
import Loader from "../../components/common/Loader/Loader";
import CardWithTabs from "../../components/common/CardWithTabs/CardWithTabs";
import dayjs from "dayjs";
import {
  fetchPresurveyDataThunk,
  fetchPillarDataThunk,
  savePresurveyDataThunk,
  resetForm,
  setFormField,
  setTabKey,
  setCdpSaved,
  setSavedFocus,
  setpreSave,
  goToNextTabThunk

} from "../../redux/slices/presurveySlice";
import {
  CONFIRMATION_MESSAGES,
  NOTIFICATION_MESSAGES,
  DATE_FORMAT,
} from "../../util/Constants";
import { fetchSuppliers } from "../../redux/slices/supplierFormSlice";
import '../../styles/global.css';
import Report from "./reporting/report/Report";
import '../../../src/index.css'

import Cdp from "./reporting/Cdp";
import { BUTTON_TEXT, API_BASE_URL } from "../../constants/Constants";
import { DownloadOutlined } from "@ant-design/icons";
import PDFReport from "./reporting/report/PDFReport";
import { saveCdpData } from "../../redux/slices/cdpslice";

const Reporting = () => {
  const [pagePermission, setPagePermission] = useState({});

  useEffect(() => {
    const permission = sessionStorage.getItem('UserPermission');
    console.log( permission);
    
      const parsedPermissions = JSON.parse(permission);
      console.log(parsedPermissions['REPORTING']);
      
      setPagePermission(parsedPermissions['REPORTING']);

  }, []);
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const state = useSelector((state) => state.presurvey);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalConfig, setModalConfig] = useState({});
  const [actionType, setActionType] = useState("");
  const [loading, setLoading] = useState(true);
  const { supplier } = useSelector((state) => state.supplier);
  const [isDraft, setIsDraft] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const cdpRef = useRef(null);
  const SUPPLIER_ID=sessionStorage.getItem("supplierId");
  const [pdfKey, setPdfKey] = useState(Date.now()); // Use a key to force remount
  
  useEffect(() => {
    dispatch(setTabKey('preSurvey'));
  }, [dispatch]);

  useEffect(() => {
    setLoading(true);
    dispatch(fetchSuppliers(SUPPLIER_ID)).finally(() =>
      setLoading(false)
    );
  }, [dispatch]);

  useEffect(() => {
    if (supplier) {
      setLoading(true);
      dispatch(fetchPresurveyDataThunk(SUPPLIER_ID)).finally(() =>
        setLoading(false)
      );
      setLoading(true);
      dispatch(fetchPillarDataThunk()).finally(() => setLoading(false));
    }
  }, [supplier, dispatch]);

  const validateForm = async () => {
    try {
      await form.validateFields(); 
      setFormErrors({});
      return true;
    } catch (errorInfo) {
      const errors = {};
      errorInfo.errorFields.forEach(({ name, errors: fieldErrors }) => {
        fieldErrors.forEach(error => {
          errors[name[0]] = error;
        });
      });
      setFormErrors(errors);
      return false;
    }
  };

  const handleReset = async () => {
    Modal.confirm({
      title: CONFIRMATION_MESSAGES.RESET.title,
      content: CONFIRMATION_MESSAGES.RESET.message,
      okText: CONFIRMATION_MESSAGES.RESET.okText,
      okType: CONFIRMATION_MESSAGES.RESET.okType,
      cancelText: CONFIRMATION_MESSAGES.RESET.cancelText,
     
      onOk: async () => {
        try {
          // Reset form fields in UI
          if (state.activeTabKey === "cdpDetails" && cdpRef.current) {
            cdpRef.current.resetFields();
            form.resetFields();
            dispatch(resetForm());
          } else {
            form.resetFields();
            dispatch(resetForm());
          }
          
          // Call API to reset data in the backend
          const response = await fetch(`${API_BASE_URL}/reportsBySupplier/reset/${SUPPLIER_ID}`, {
            method: 'DELETE',
            headers:{
              Authorization:`Bearer ${sessionStorage.getItem("authToken")}`,
             'Content-Type': 'application/json'
            }
          });
          
          if (response.ok) {
            // Force remount of PDFReport component by changing its key
            setPdfKey(Date.now());
            
            notification.success({
              message: NOTIFICATION_MESSAGES.resetSuccessful.message,
              description: NOTIFICATION_MESSAGES.resetSuccessful.description,
            });
          } else {
            throw new Error('Failed to reset data on server');
          }
        } catch (err) {
          notification.error({
            message: NOTIFICATION_MESSAGES.resetFailed.message,
            description: NOTIFICATION_MESSAGES.resetFailed.description(err),
          });
        }
      },
    });
  };
  
  const handleSave = async () => {
    const isValid = await validateForm();

    if (isValid) {
      setModalConfig(CONFIRMATION_MESSAGES.SAVE);
      setActionType("save");
      setModalVisible(true);
      setIsDraft(false);
    }
  };

  const goToNextTab = () => {
    dispatch(goToNextTabThunk())
  };

  const handleModalConfirm = useCallback(async () => {
    if (actionType === "next") {
      dispatch(setCdpSaved(state.cdpDataAvailable));
      dispatch(setpreSave(state.otherDataToReport));
    
        dispatch(setSavedFocus(state.focusAreas))
  
      setIsDraft(false);
      goToNextTab();
    } else if (actionType === "reset") {
      form.resetFields();
      dispatch(resetForm());
    } else if (actionType === "save") {
      try {
        if (state.activeTabKey === 'cdpDetails' && cdpRef.current) {
          await cdpRef.current.saveData();
        } else {
          const values = await form.validateFields();
          const pillarData = state.pillarData.reduce((acc, pillar) => {
            acc[pillar.pillarId] = pillar.pillarName;
            return acc;
          }, {});

          const focusAreas = Object.keys(state.focusAreas).reduce(
            (acc, pillarId) => {
              const pillarName = pillarData[pillarId];
              acc[pillarName] = state.focusAreas[pillarId];
              return acc;
            },
            {}
          );
          const dataToSave = {
            ...values,
            supplierId: SUPPLIER_ID,
            supplierName: supplier?.companyName || "new supplier",
            reportCreationYear: state.reportCreationYear,
            periodStartDate: state.periodStartDate,
            periodEndDate: state.periodEndDate,
            cdpDataAvailable: state.cdpDataAvailable,
            otherDataToReport: state.otherDataToReport,
            focusAreas: state.focusAreas? state.focusAreas:{},
            savedFocusAreas:state.focusAreas? state.savedFocusAreas:{},
            savedCdp:state.cdpDataAvailable? state.cdpDataAvailable:false,
            createdDate: dayjs(),
            createdUser: "user",
            updatedDate: dayjs(),
            updatedUser: "user",
          };

          const isExistingUser = false;

          if (isExistingUser) {

            console.log("Existing USer",SUPPLIER_ID,dataToSave)
            await dispatch(
              savePresurveyDataThunk({
                supplierId: state.supplierId,
                data: dataToSave,
              })
            ).unwrap();
          } else {
            console.log("Non Existing USer",SUPPLIER_ID,dataToSave)
            await dispatch(
              savePresurveyDataThunk({
                supplierId: SUPPLIER_ID,
                data: dataToSave,
              })
            ).unwrap();
          }
          setIsDraft(false);
          await dispatch(fetchPresurveyDataThunk(SUPPLIER_ID)).unwrap();
          
          // Force remount of PDFReport component by changing its key
          setPdfKey(Date.now());
          
          notification.success(NOTIFICATION_MESSAGES.SAVE_SUCCESS);
        }
      } catch (error) {
        console.error("Error saving data:", error);
        notification.error(NOTIFICATION_MESSAGES.SAVE_FAILED);
      }
    }
    setModalVisible(false);
  }, [form, dispatch, state, actionType, supplier]);

  const handleNext = async () => {
    const isValid = await validateForm();
  
    if (isValid) {
      if (state.activeTabKey === "preSurvey") {
      
        if (isDraft) {
          dispatch({ type: 'presurvey/setTabKey', payload: 'preSurvey' });
          setModalConfig(CONFIRMATION_MESSAGES.NEXT);
          setActionType("next");
          setModalVisible(true);
        } else {
          goToNextTab(); 
        }
      } else if (state.activeTabKey === "cdpDetails") {
       
        if (cdpRef.current?.hasChanges) {
          setModalConfig(CONFIRMATION_MESSAGES.NEXT);
          setActionType("next");
          setModalVisible(true);
        } else {
          goToNextTab(); 
        }
      }
    }
  };
  
  const handleFieldChange = (field) => (e) => {
    dispatch(setFormField({ field, value: e.target.value }));
    setIsDraft(true);
  };

  const handleDateChange = (field) => (date, dateString) => {
    dispatch(
      setFormField({ field, value: date ? date.format(DATE_FORMAT) : null })
    );
    setIsDraft(true);
  };

  const handleSwitchChange = (field) => (checked) => {
    dispatch(setFormField({ field, value: checked }));
    setIsDraft(true);
  };

  const handleCheckboxChange = (pillarId) => (e) => {
    const checked = e.target.checked;
    const updatedFocusAreas = checked
      ? { ...state.focusAreas, [pillarId]: true }
      : { ...state.focusAreas, [pillarId]: false };

    dispatch(setFormField({ field: "focusAreas", value: updatedFocusAreas }));
    setIsDraft(true);
  };

  const contentListNoTitle = {
    preSurvey: (
      <PreSurvey
        form={form}
        handleFieldChange={handleFieldChange}
        handleDateChange={handleDateChange}
        handleSwitchChange={handleSwitchChange}
        handleCheckboxChange={handleCheckboxChange}
        setIsDraft={setIsDraft}
        formErrors={formErrors}
      />
    ),
  };
 
  if(state.savedCdp){
    contentListNoTitle.cdpDetails=<Cdp ref={cdpRef} setIsDraft={setIsDraft}/>
  }
  if(state.savedOtherDataToReport){
    contentListNoTitle.reporting= <Report/>
  }
  if(pagePermission===false){
    console.log(pagePermission);
    
    return <div>You do not have permission to access Reporting.</div>;
  }
  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <>
        {state?.activeTabKey=="reporting" && (
        <div className="flex justify-end lg:mr-[36px] md:mr-4 sm:mr-1">
          <PDFReport key={pdfKey} supplierId={SUPPLIER_ID} />
        </div>
        )}
          <CardWithTabs
            tabList={state.tabList}
            activeTabKey={state?.activeTabKey}
            onTabChange={async (newActiveTabKey) => {
              if (state.activeTabKey === 'cdpDetails' && cdpRef.current?.hasChanges) {
                const isConfirmed = window.confirm(CONFIRMATION_MESSAGES.TABCHANGE);
                if (isConfirmed) {
                  const saveSuccess = await cdpRef.current.saveData();
                  if (saveSuccess) {
                    dispatch(setTabKey(newActiveTabKey));
                  }
                }
              } else {
                dispatch(setTabKey(newActiveTabKey));
              }
            }}
          >
            {contentListNoTitle[state.activeTabKey]}
          </CardWithTabs>

          <FooterButtonGroup
            onReset={handleReset}
            onSave={handleSave}
            onNext={handleNext}
            activeTabKey={state.activeTabKey}
            id="button-group"
            tabList={contentListNoTitle}
          />
          <ConfirmationModal
            visible={modalVisible}
            title={modalConfig.title}
            message={modalConfig.message}
            onConfirm={handleModalConfirm}
            onCancel={() => setModalVisible(false)}
            id="confirmation-modal"
          />
        </>
      )}
    </>
  );
};

export default Reporting;